import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getConfidenceTrend, getChartVolume, getChartClassification, getChartConfidence, getAuditStats } from '../services/api';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { TrendUp, Brain, ShieldCheck, ChartLine } from '@phosphor-icons/react';

const COLORS = ['#09090B', '#F59E0B', '#3B82F6', '#10B981', '#EF4444'];

function CustomTooltip({ active, payload, label }) {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-3 shadow-sm">
        <p className="text-xs font-mono text-zinc-500 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={p.dataKey || p.name || `payload-${i}`} className="text-sm font-medium">
            {p.name}: <span className="font-mono">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function AnalyticsPage() {
  const [trendData, setTrendData] = useState([]);
  const [volumeData, setVolumeData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [confData, setConfData] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [trendRes, volRes, classRes, confRes, auditRes] = await Promise.all([
        getConfidenceTrend(),
        getChartVolume(),
        getChartClassification(),
        getChartConfidence(),
        getAuditStats()
      ]);
      setTrendData(trendRes.data.data);
      setVolumeData(volRes.data.data);
      setClassData(classRes.data.data);
      setConfData(confRes.data.data);
      setAuditStats(auditRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Calculate trend direction
  const avgFirst = trendData.length >= 2 ? trendData.slice(0, Math.ceil(trendData.length / 2)).reduce((a, b) => a + b.avg_confidence, 0) / Math.ceil(trendData.length / 2) : 0;
  const avgLast = trendData.length >= 2 ? trendData.slice(Math.ceil(trendData.length / 2)).reduce((a, b) => a + b.avg_confidence, 0) / (trendData.length - Math.ceil(trendData.length / 2)) : 0;
  const trendDirection = avgLast > avgFirst ? 'improving' : avgLast < avgFirst ? 'declining' : 'stable';

  return (
    <Layout title="Analytics">
      <div data-testid="analytics-page" className="space-y-6">
        {/* Confidence Trend - Hero Chart */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="confidence-trend-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TrendUp size={20} weight="duotone" className="text-violet-600" />
                <h2 className="font-semibold">AI Confidence Trend</h2>
              </div>
              <p className="text-sm text-zinc-500">
                Tracks how AI classification confidence evolves as more context rules and corrections are added.
              </p>
            </div>
            {trendData.length >= 2 && (
              <div className={`px-3 py-1.5 text-xs uppercase tracking-widest border rounded-sm ${
                trendDirection === 'improving' ? 'bg-emerald-50 text-emerald-700 border-emerald-500' :
                trendDirection === 'declining' ? 'bg-red-50 text-red-700 border-red-500' :
                'bg-zinc-100 text-zinc-600 border-zinc-300'
              }`}>
                {trendDirection === 'improving' ? 'Improving' : trendDirection === 'declining' ? 'Declining' : 'Stable'}
              </div>
            )}
          </div>
          
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="confGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="avg_confidence" name="Avg Confidence" stroke="#8B5CF6" fill="url(#confGradient)" strokeWidth={2} />
                <Line type="monotone" dataKey="max" name="Max" stroke="#10B981" strokeDasharray="3 3" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="min" name="Min" stroke="#EF4444" strokeDasharray="3 3" strokeWidth={1} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-zinc-400 text-sm">
              Analyze transactions to see confidence trends over time
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Avg Confidence</p>
            <p className="text-3xl font-bold font-mono">
              {trendData.length > 0 ? Math.round(trendData.reduce((a, b) => a + b.avg_confidence, 0) / trendData.length) : 0}%
            </p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Analyzed Total</p>
            <p className="text-3xl font-bold font-mono">
              {trendData.reduce((a, b) => a + b.count, 0)}
            </p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Context Overrides</p>
            <p className="text-3xl font-bold font-mono">{auditStats?.total_overrides || 0}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Override Scenarios</p>
            <p className="text-3xl font-bold font-mono">{Object.keys(auditStats?.by_scenario || {}).length}</p>
          </div>
        </div>

        {/* Detail Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Volume */}
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Transaction Volume</h3>
            {volumeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={volumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#09090B" radius={[2, 2, 0, 0]} name="Transactions" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[250px] flex items-center justify-center text-zinc-400 text-sm">No data</div>}
          </div>

          {/* Confidence Distribution */}
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Confidence Distribution</h3>
            {confData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={confData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="range" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Transactions" radius={[2, 2, 0, 0]}>
                    {confData.map((entry, i) => <Cell key={entry.range || `conf-${i}`} fill={['#EF4444', '#F59E0B', '#F59E0B', '#3B82F6', '#10B981'][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[250px] flex items-center justify-center text-zinc-400 text-sm">No analyzed transactions</div>}
          </div>
        </div>

        {/* Override Breakdown */}
        {auditStats?.by_scenario && Object.keys(auditStats.by_scenario).length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={18} weight="duotone" className="text-zinc-600" />
              <h3 className="text-xs uppercase tracking-widest text-zinc-500">Override Scenarios Breakdown</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(auditStats.by_scenario).map(([scenario, count]) => (
                <div key={scenario} className="bg-zinc-50 border border-zinc-100 rounded-sm p-4 text-center">
                  <p className="text-2xl font-mono font-bold">{count}</p>
                  <p className="text-xs text-zinc-500 capitalize mt-1">{scenario.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
